import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { baseURL, headers } from '@/v2/constants/requests'
import { readError } from '@/v2/hooks/appointments/useUnconfirmedAppointments'

const QUERY_KEY = ['SettingsCancellationNotice']

const FIELD_LABELS = {
  sms_message: 'SMS message',
  email_subject: 'Email subject',
  email_message: 'Email message',
  booking_page: 'Booking page',
  reason_not_confirmed: "Didn't confirm",
  reason_health_history: 'Health history not completed',
  reason_both: 'Both',
}

// docs/v2/private/rest/settings/cancellation_notice_doc.md
// { bySms, byEmail, bookingPageId, bookingPageUrl, smsMessage, emailSubject,
//   emailMessage, reasons: { notConfirmed, healthHistory, both } }
// The three messages are Liquid templates, like the reminder templates.
export const useSettingsCancellationNotice = () => {
  const { data, isLoading, isError } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const res = await fetch(
        `${baseURL}/v2/rest/settings/cancellation_notice`,
        { headers }
      )
      if (!res.ok) {
        throw new Error("We couldn't load the cancellation notice")
      }
      return res.json()
    },
  })

  return { notice: data, isLoading, isError }
}

// Admin only: anyone else gets a 404.
export const useUpdateCancellationNotice = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: updateNotice, ...rest } = useMutation({
    mutationFn: async notice => {
      const res = await fetch(
        `${baseURL}/v2/rest/settings/cancellation_notice`,
        {
          method: 'PATCH',
          headers,
          body: JSON.stringify({
            cancellation_notice: {
              by_sms: notice.bySms,
              by_email: notice.byEmail,
              booking_page_id: notice.bookingPageId,
              sms_message: notice.smsMessage,
              email_subject: notice.emailSubject,
              email_message: notice.emailMessage,
              reason_not_confirmed: notice.reasons.notConfirmed,
              reason_health_history: notice.reasons.healthHistory,
              reason_both: notice.reasons.both,
            },
          }),
        }
      )
      if (!res.ok) {
        throw new Error(
          res.status === 404
            ? 'Only admins can change the cancellation notice'
            : await readError(
                res,
                'Could not save the cancellation notice',
                FIELD_LABELS
              )
        )
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEY)
    },
  })

  return { updateNotice, ...rest }
}
